import { useLocation, Link } from "react-router-dom"
import { ChevronRight, Home } from "lucide-react"
import { cn } from "@/lib/utils"

const pageNames: Record<string, string> = {
  "/": "Home",
  "/admob": "AdMob Revenue",
  "/analytics": "App Analytics",
  "/settings": "Settings",
}

interface PageHeaderProps {
  icon?: string
  title?: string
  description?: string
  children?: React.ReactNode
}

export function PageHeader({ 
  icon, 
  title, 
  description, 
  children 
}: PageHeaderProps) {
  const location = useLocation()
  const pathParts = location.pathname.split("/").filter(Boolean)
  const currentPageName = pageNames[location.pathname] || "Page"

  return (
    <div className="mb-8">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link 
          to="/" 
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <Home className="h-3.5 w-3.5" />
          <span>Home</span>
        </Link>
        {pathParts.map((part, index) => {
          const path = "/" + pathParts.slice(0, index + 1).join("/")
          const name = pageNames[path] || part
          const isLast = index === pathParts.length - 1
          
          return (
            <div key={path} className="flex items-center gap-2">
              <ChevronRight className="h-3.5 w-3.5" />
              {isLast ? (
                <span className="text-foreground font-medium">{name}</span>
              ) : (
                <Link 
                  to={path}
                  className="hover:text-foreground transition-colors"
                >
                  {name}
                </Link>
              )}
            </div>
          )
        })}
      </nav>

      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          {icon && (
            <div className="text-5xl leading-none">{icon}</div>
          )}
          <div>
            <h1 className={cn(
              "text-3xl font-bold tracking-tight",
              !icon && "text-4xl"
            )}>
              {title || currentPageName}
            </h1>
            {description && (
              <p className="text-muted-foreground mt-1">{description}</p>
            )}
          </div>
        </div>
        {children && (
          <div className="flex items-center gap-2">
            {children}
          </div>
        )}
      </div>
    </div>
  )
}
